// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

export interface RequestCache {
  run<T>(key: string, load: () => Promise<T>): Promise<T>;

  invalidate(keyPrefix?: string): void;
}

export function createRequestCache(
  ttlMs: number,
  now: () => number = Date.now,
): RequestCache {
  const entries = new Map<string, { pending: Promise<unknown>; expiresAt: number }>();

  return {
    run<T>(key: string, load: () => Promise<T>): Promise<T> {
      const startedAt = now();
      const cached = entries.get(key);
      if (cached !== undefined) {
        if (cached.expiresAt > startedAt) {
          return cached.pending as Promise<T>;
        }
        entries.delete(key);
      }

      const pending = load();
      entries.set(key, { pending, expiresAt: startedAt + ttlMs });
      void pending.catch(() => {
        if (entries.get(key)?.pending === pending) {
          entries.delete(key);
        }
      });
      return pending;
    },

    invalidate(keyPrefix?: string): void {
      if (keyPrefix === undefined) {
        entries.clear();
        return;
      }
      for (const key of [...entries.keys()]) {
        if (key.startsWith(keyPrefix)) {
          entries.delete(key);
        }
      }
    },
  };
}